/**
 * Google Ads OAuth 2.0 Authentication Service
 * Handles OAuth flow and token management for Google Ads API
 * 
 * SECURITY: Tokens are encrypted using AES-256-GCM before storage in Firestore.
 * See tokenEncryption.ts for implementation details.
 */

import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { GoogleAdsConnection } from '../types/googleAds';
import { encryptToken, decryptToken, isTokenEncrypted } from './tokenEncryption';

// OAuth Configuration
const GOOGLE_ADS_CLIENT_ID = import.meta.env.VITE_GOOGLE_ADS_CLIENT_ID || '';
const GOOGLE_ADS_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_ADS_CLIENT_SECRET || '';
const REDIRECT_URI = import.meta.env.VITE_GOOGLE_ADS_REDIRECT_URI || 
  `${window.location.origin}/oauth/google-ads/callback`;

const OAUTH_SCOPES = 'https://www.googleapis.com/auth/adwords';

export const googleAdsAuth = {
  /**
   * Initiate OAuth 2.0 flow
   * Redirects user to Google authorization page
   */
  initiateOAuthFlow(orgId: string, customerId: string): void {
    // Store orgId and customerId in session storage for callback
    sessionStorage.setItem('googleAds_oauth_orgId', orgId);
    sessionStorage.setItem('googleAds_oauth_customerId', customerId);
    
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_ADS_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', OAUTH_SCOPES);
    authUrl.searchParams.set('access_type', 'offline'); // Request refresh token
    authUrl.searchParams.set('prompt', 'consent'); // Force consent to get refresh token
    authUrl.searchParams.set('state', `${orgId}|${customerId}`); // Pass both in state
    
    console.log('🔐 Redirecting to Google OAuth...');
    window.location.href = authUrl.toString();
  },

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleOAuthCallback(code: string, state: string): Promise<{
    success: boolean;
    orgId: string;
    error?: string;
  }> {
    try {
      // Extract orgId and customerId from state
      const [orgIdFromState, customerIdFromState] = state ? state.split('|') : ['', ''];
      const orgId = orgIdFromState || sessionStorage.getItem('googleAds_oauth_orgId') || '';
      const customerId = customerIdFromState || sessionStorage.getItem('googleAds_oauth_customerId') || '';
      
      if (!orgId) {
        throw new Error('Organization ID not found in OAuth state');
      }
      
      if (!customerId) {
        throw new Error('Customer ID not found in OAuth state');
      }

      console.log('🔄 Exchanging authorization code for tokens...');
      console.log(`📋 Customer ID: ${customerId}`);

      // Exchange authorization code for tokens
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          code,
          client_id: GOOGLE_ADS_CLIENT_ID,
          client_secret: GOOGLE_ADS_CLIENT_SECRET,
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code'
        })
      });

      if (!tokenResponse.ok) {
        const error = await tokenResponse.json();
        throw new Error(error.error_description || 'Failed to exchange authorization code');
      }

      const tokens = await tokenResponse.json();
      
      // Save tokens and customer ID to Firestore (should be encrypted in production!)
      await this.saveTokens(orgId, customerId, {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresIn: tokens.expires_in
      });

      console.log('✅ OAuth tokens saved successfully');
      console.log(`✅ Customer ID saved: ${customerId}`);
      
      // Clear session storage
      sessionStorage.removeItem('googleAds_oauth_orgId');
      sessionStorage.removeItem('googleAds_oauth_customerId');

      return {
        success: true,
        orgId
      };
    } catch (error: any) {
      console.error('❌ OAuth callback error:', error);
      return {
        success: false,
        orgId: '',
        error: error.message
      };
    }
  },

  /**
   * Save OAuth tokens to Firestore
   * SECURITY: Tokens are encrypted using AES-256-GCM before storage
   */
  async saveTokens(
    orgId: string,
    customerId: string,
    tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }
  ): Promise<void> {
    const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
    
    const expiresAt = new Date(Date.now() + tokens.expiresIn * 1000);
    
    // Encrypt tokens before storing
    const [encryptedAccessToken, encryptedRefreshToken] = await Promise.all([
      encryptToken(tokens.accessToken, orgId),
      encryptToken(tokens.refreshToken, orgId)
    ]);
    
    await setDoc(connectionRef, {
      isConnected: true,
      status: 'connected',
      customerId: customerId,
      accountName: `Google Ads Account ${customerId}`,
      // Tokens are now encrypted with AES-256-GCM
      accessToken: encryptedAccessToken,
      refreshToken: encryptedRefreshToken,
      tokensEncrypted: true, // Flag to indicate encryption status
      tokenExpiresAt: Timestamp.fromDate(expiresAt),
      connectedAt: Timestamp.now(),
      lastSyncAt: Timestamp.now(),
      apiVersion: 'v22'
    }, { merge: true });
    
    console.log('🔐 Tokens encrypted and saved to Firestore');
  },

  /**
   * Get valid access token (auto-refresh if expired)
   * SECURITY: Automatically decrypts tokens if they are encrypted
   */
  async getAccessToken(orgId: string): Promise<string> {
    const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
    const connectionDoc = await getDoc(connectionRef);
    
    if (!connectionDoc.exists()) {
      throw new Error('Google Ads not connected');
    }
    
    const data = connectionDoc.data();
    const tokenExpiresAt = data.tokenExpiresAt?.toDate();
    const now = new Date();
    
    // Decrypt tokens if they are encrypted
    let accessToken = data.accessToken;
    let refreshToken = data.refreshToken;
    
    if (data.tokensEncrypted || isTokenEncrypted(accessToken)) {
      accessToken = await decryptToken(accessToken, orgId);
      refreshToken = await decryptToken(refreshToken, orgId);
    }
    
    // Check if token is still valid (with 5 min buffer)
    if (tokenExpiresAt && tokenExpiresAt.getTime() - now.getTime() > 5 * 60 * 1000) {
      // Token still valid
      return accessToken;
    }
    
    // Token expired or about to expire - refresh it
    console.log('🔄 Access token expired, refreshing...');
    return await this.refreshAccessToken(orgId, refreshToken);
  },

  /**
   * Refresh access token using refresh token
   * SECURITY: New access token is encrypted before storage
   */
  async refreshAccessToken(orgId: string, refreshToken: string): Promise<string> {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          refresh_token: refreshToken,
          client_id: GOOGLE_ADS_CLIENT_ID,
          client_secret: GOOGLE_ADS_CLIENT_SECRET,
          grant_type: 'refresh_token'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh access token');
      }

      const data = await response.json();
      const newAccessToken = data.access_token;
      const expiresIn = data.expires_in;
      
      // Encrypt the new access token before storing
      const encryptedAccessToken = await encryptToken(newAccessToken, orgId);
      
      // Update access token in Firestore
      const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
      await updateDoc(connectionRef, {
        accessToken: encryptedAccessToken,
        tokensEncrypted: true,
        tokenExpiresAt: Timestamp.fromDate(new Date(Date.now() + expiresIn * 1000)),
        lastSyncAt: Timestamp.now()
      });

      console.log('✅ Access token refreshed and encrypted successfully');
      return newAccessToken; // Return the decrypted token for immediate use
    } catch (error) {
      console.error('❌ Error refreshing access token:', error);
      throw error;
    }
  },

  /**
   * Revoke OAuth tokens and disconnect
   * SECURITY: Decrypts tokens if needed before revoking with Google
   */
  async revokeTokens(orgId: string): Promise<void> {
    try {
      const connectionRef = doc(db, 'organizations', orgId, 'integrations', 'googleAds');
      const connectionDoc = await getDoc(connectionRef);
      
      if (connectionDoc.exists()) {
        const data = connectionDoc.data();
        
        // Revoke the refresh token
        if (data.refreshToken) {
          // Decrypt token if encrypted
          let refreshToken = data.refreshToken;
          if (data.tokensEncrypted || isTokenEncrypted(refreshToken)) {
            try {
              refreshToken = await decryptToken(refreshToken, orgId);
            } catch (e) {
              console.warn('Could not decrypt token for revocation, continuing...');
            }
          }
          
          await fetch('https://oauth2.googleapis.com/revoke', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams({
              token: refreshToken
            })
          });
        }
      }
      
      // Update Firestore - clear all sensitive data
      await updateDoc(connectionRef, {
        isConnected: false,
        status: 'disconnected',
        accessToken: null,
        refreshToken: null,
        tokensEncrypted: false,
        disconnectedAt: Timestamp.now()
      });
      
      console.log('✅ Google Ads disconnected successfully');
    } catch (error) {
      console.error('❌ Error revoking tokens:', error);
      throw error;
    }
  }
};

